import { useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { PrimitiveAtom } from 'jotai'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { contactActionAtom, contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { getRequiredContactFieldsForCryptoAddress } from 'pages/Contacts/utils/fiatInstruments'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import type { Contact } from 'types/contacts'
import { InstrumentType } from 'types/contacts'

export type PaymentFlowStep = 'contact' | 'details' | 'complete'

export interface PaymentFlowStepConfig {
  CONTACT: 'contact'
  DETAILS: 'details'
  COMPLETE: 'complete'
}

interface UsePaymentFlowParams {
  stepAtom: PrimitiveAtom<PaymentFlowStep>
  steps: readonly PaymentFlowStep[]
  stepConfig: PaymentFlowStepConfig
  translationKeys: {
    contactStepHeader: string
    detailsStepHeader: string
    requiredFieldsCallout: string
  }
}

export const usePaymentFlow = ({
  stepAtom,
  steps,
  stepConfig,
  translationKeys,
}: UsePaymentFlowParams) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { enableContactBankAccounts } = useFeatureFlags()

  const [currentStep, setCurrentStep] = useAtom(stepAtom)
  const [walletAction, setWalletAction] = useContext(WalletActionContext)
  const setContact = useSetAtom(contactAtom)

  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const isFormSteps = currentStep !== stepConfig.COMPLETE && currentStep !== stepConfig.CONTACT

  const getSheetHeader = (): string => {
    switch (currentStep) {
      case stepConfig.CONTACT:
        return t(translationKeys.contactStepHeader)
      case stepConfig.DETAILS:
        return t(translationKeys.detailsStepHeader)
      default:
        return ''
    }
  }

  const handleContactSelect = (contact: Contact) => {
    setContact(contact)
    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)

    if (enableContactBankAccounts && requiredFields.length) {
      setContactAction({
        ...contactAction,
        actionId: ContactActionIds.CONTACT,
        header: t('contacts.updateContact'),
        onClose: () => {
          queryClient.invalidateQueries({ queryKey: ['contacts'] })
          queryClient.refetchQueries({ queryKey: ['contactsPayment'] })
          setIsContactSheetOpen(false)
        },
        meta: {
          keepUpdatedContact: true,
          flowType: InstrumentType.CRYPTO,
          contactRequiredFields: {
            requiredFields,
            calloutMessage: t(translationKeys.requiredFieldsCallout),
          },
        },
      })
      setIsContactSheetOpen(true)
      return
    }

    setCurrentStep(stepConfig.DETAILS)
  }

  const handleOnBack = () => {
    const currentStepIndex = steps.indexOf(currentStep)
    setCurrentStep(steps[currentStepIndex - 1])
    setContact(null)
  }

  const updateWalletAction = () => {
    setWalletAction({
      ...walletAction,
      header: getSheetHeader(),
      onBack: isFormSteps ? handleOnBack : null,
      meta: {
        ...walletAction?.meta,
        flowType: InstrumentType.CRYPTO,
        handleContactSelect: walletAction?.meta?.handleContactSelect
          ? walletAction?.meta?.handleContactSelect
          : handleContactSelect,
      },
    })
  }

  return {
    currentStep,
    isContactSheetOpen,
    updateWalletAction,
  }
}
